// ============================================================
// FIXED: ReportHeader Component
// ============================================================
// src/features/reports/components/ReportHeader.tsx

import { ArrowLeft, Filter, FileText, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';

interface ReportHeaderProps {
    title: string;
    description?: string;
    icon: React.ElementType;
    iconGradient: string;
    hasFilters: boolean;
    showFilters: boolean;
    onToggleFilters: () => void;
    onExportPdf: () => void;
    onExportExcel: () => void;
    branchName?: string;
}

export function ReportHeader({
    title, description, icon: Icon, iconGradient,
    hasFilters, showFilters, onToggleFilters,
    onExportPdf, onExportExcel, branchName
}: ReportHeaderProps) {
    const navigate = useNavigate();

    return (
        <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/reports')}
                            className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                            type="button"
                        >
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div className={`p-2.5 bg-gradient-to-br ${iconGradient} rounded-2xl shadow-lg`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">{title}</h1>
                            <p className="text-sm text-slate-500">
                                {hasFilters ? 'Filtered view' : 'All time'}
                                {branchName ? ` · ${branchName}` : ' · All Branches'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onToggleFilters}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border',
                                showFilters
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-white border-slate-200 text-slate-700'
                            )}
                            type="button"
                        >
                            <Filter size={14} /> Filters
                        </button>
                        <button
                            onClick={onExportPdf}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium shadow-sm"
                            type="button"
                        >
                            <FileText size={14} /> PDF
                        </button>
                        <button
                            onClick={onExportExcel}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium shadow-sm"
                            type="button"
                        >
                            <FileSpreadsheet size={14} /> Excel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}